import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import makeStyles from '@mui/styles/makeStyles';
import {
  AppBar,
  Breadcrumbs,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  type Theme,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import DoneIcon from '@mui/icons-material/Done';
import PrintIcon from '@mui/icons-material/Print';
import {
  useApplicationFilesStore,
  useApplicationPdfStore,
  useApproveCreditLimitStore,
  useAssignApplicationStore,
  useCountriesStore,
  useDeleteApplicationFileStore,
  useDownloadApplicationFileStore,
  useRejectApplication2Store,
  useRejectApplicationStore,
  useRejectCreditLimitStore,
  useUsersStore,
} from '@lib';
import { ApplicationEntity, SupplementaryApplicationEntity, FileApi, ApplicationApi, ApiUtils } from '@shared/api';
import { PermissionsUtils, RoutePaths, useRouteHook } from '@shared';
import { ValidateForm } from './utils';
import { PermissionLevel, PermissionType, RoleType } from '@shared/types';
import { ApplicationType } from '@shared/types/ApplicationType';
import { FileList } from './widgets';
import { PDFView } from '@widgets/PDFView';
import { FileUpload } from '@widgets/FileUpload/FileUpload';
import { Button, IconButton, DelayedLinearProgress, RouterNavLink, SnackbarContext } from '@widgets';

const useStyles = makeStyles<Theme>((theme) => ({
  postTransactionContainer: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.primary.main,
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  cardDesign: {
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.primary.contrastText,
  },
  appBar: {
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
  },
  title: {
    display: 'none',
    color: theme.palette.common.black,
    [theme.breakpoints.up('sm')]: {
      display: 'block',
    },
  },
  grow: {
    flexGrow: 1,
  },
  toolbarIcon: {
    color: theme.palette.primary.contrastText,
  },
  dropArea: {
    textAlign: 'center',
    padding: theme.spacing(7),
  },
}));

const getApplicationCurrency = (applicationData: ApplicationEntity): string => {
  const result: string[] = [];
  if (applicationData.chfCurrency) result.push('CHF');
  if (applicationData.eurCurrency) result.push('EUR');
  if (applicationData.usdCurrency) result.push('USD');
  return result.length > 0 ? result.join(', ') : 'None';
};

const getApplicationCreditLimit = (applicationData: ApplicationEntity): string => {
  const result: string[] = [];
  if (applicationData.chfCurrency) result.push(applicationData.chfCreditLimit);
  if (applicationData.eurCurrency) result.push(applicationData.eurCreditLimit);
  if (applicationData.usdCurrency) result.push(applicationData.usdCreditLimit);
  return result.length > 0 ? result.join(', ') : 'None';
};

const getApplicationProduct = (
  applicationData: ApplicationEntity | SupplementaryApplicationEntity | null | undefined,
  mainApplicationData: ApplicationEntity | null | undefined,
): string => {
  if (!applicationData) return '';
  if (applicationData.applicationType == ApplicationType.MAIN) return (applicationData as ApplicationEntity).productName || '(none)';
  if (!mainApplicationData) return '';
  return (mainApplicationData.productName || '(none)') + ' Supplementary';
};

const getRouteToEdit = (appId: string | undefined, mainAppId: string | undefined, applicationType?: string) =>
  applicationType === ApplicationType.MAIN
    ? RoutePaths.applicationsEdit.onNavigate(appId!)
    : RoutePaths.applicationsSupplementaryEdit.onNavigate(mainAppId!, appId!);
const getRouteToPrint = (appId: string | undefined, mainAppId: string | undefined, applicationType?: string) =>
  applicationType === ApplicationType.MAIN
    ? RoutePaths.applicationsDetails.onNavigate(appId!)
    : RoutePaths.applicationsSupplementaryDetails.onNavigate(mainAppId!, appId!);

const LOADING_TITLE = 'Loading application details';

type Params = {
  selectedApplicationId: string;
  mainApplicationId?: string;
};

export interface Props {
  supplementary?: boolean;
}

export const ApplicationDetailsPage = ({ supplementary }: Props) => {
  const { selectedApplicationId, mainApplicationId } = useParams<Params>();
  const { navigate } = useRouteHook();

  const { countries, getCountries } = useCountriesStore();
  const { users: managers, getUsers } = useUsersStore();
  const { loading: loadingApplicationFiles, applicationFiles: fileData, getApplicationFiles } = useApplicationFilesStore();
  const { loading: loadingDownloadApplicationFile } = useDownloadApplicationFileStore();
  const { loading: loadingDeleteApplicationFile, deleteApplicationFiles } = useDeleteApplicationFileStore();
  const { file, getApplicationPdf, clear: clearApplicationPdf } = useApplicationPdfStore();
  const {
    loading: loadingApproveCreditLimit,
    success: successApprovedCreditLimit,
    approveCreditLimit,
    clear: clearApprovedCreditLimit,
  } = useApproveCreditLimitStore();
  const {
    loading: loadingRejectCreditLimit,
    success: successRejectedCreditLimit,
    rejectCreditLimit,
    clear: clearRejectedCreditLimit,
  } = useRejectCreditLimitStore();
  const {
    loading: loadingRejectApplication,
    success: successRejectedApplication,
    rejectApplication,
    clear: clearRejectedApplication,
  } = useRejectApplicationStore();
  const {
    loading: loadingRejectApplication2,
    error: errorRejectApplication2,
    success: successRejectedApplication2,
    rejectApplication: rejectApplication2,
    clear: clearRejectedApplication2,
  } = useRejectApplication2Store();
  const {
    loading: loadingAssignApplication,
    success: successAssignedApplication,
    assignApplication,
    clear: clearAssignApplication,
  } = useAssignApplicationStore();

  const [applicationData, setApplicationData] = useState<ApplicationEntity | SupplementaryApplicationEntity | undefined | null>();
  const [mainApplicationData, setMainApplicationData] = useState<ApplicationEntity | undefined | null>();
  const [securityChanged, setSecurityChanged] = useState(false);
  const [loadingApplication, setLoadingApplication] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [version, setVersion] = useState<number>(0);
  const [saveSecurityError, setSaveSecurityError] = useState(false);
  const [selectedApplicationType, setSelectedApplicationType] = useState<string | undefined>();
  const [fileReload, setFileReload] = useState<boolean>(false);
  const [rejectingCreditLimit, setRejectingCreditLimit] = useState<boolean>(false);
  const [rejectQuestionVisible, setRejectQuestionVisible] = useState<boolean>(false);
  const [rejectType, setRejectType] = useState<string>('limit');

  const classes = useStyles();
  const snackbar = useContext(SnackbarContext);

  useEffect(() => {
    getCountries();
    getUsers();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoadingApplication(true);
        // setApplicationData(undefined);
        setMainApplicationData(undefined);
        const suppId: string = supplementary ? selectedApplicationId! : '';
        const mainId: string = supplementary ? mainApplicationId! : selectedApplicationId!;
        const [suppApp, mainApp] = await Promise.all([
          supplementary ? ApplicationApi.getSupplementaryApplication(suppId) : Promise.resolve<SupplementaryApplicationEntity | null>(null),
          ApplicationApi.getMainApplication(mainId),
        ]);
        setApplicationData(supplementary ? suppApp : mainApp);
        setMainApplicationData(mainApp);
        if (suppApp) setSelectedApplicationType(suppApp.applicationType);
        else if (mainApp) setSelectedApplicationType(mainApp.applicationType);
      } catch (error) {
        snackbar.error(ApiUtils.getError(error));
      } finally {
        setLoadingApplication(false);
      }
    })();
  }, [selectedApplicationId, mainApplicationId, version, supplementary]);

  useEffect(() => {
    if (!selectedApplicationId) return;
    getApplicationFiles(selectedApplicationId);
  }, [selectedApplicationId, fileReload, version]);

  const canUpdate = applicationData?.status === 'Draft' && PermissionsUtils.hasPermission(PermissionType.APPLICATIONS, PermissionLevel.EDIT);
  const canEditOrSubmit = PermissionsUtils.hasPermission(PermissionType.APPLICATIONS, PermissionLevel.EDIT) && applicationData?.status === 'Draft';
  const currentManager = useMemo(() => {
    if (applicationData && managers) {
      return managers.find((item) => item.id === applicationData.partnerAccountManagerId);
    } else return undefined;
  }, [applicationData, managers]);
  const canSubmit = useMemo(() => {
    return (
      canEditOrSubmit &&
      !!fileData.length &&
      fileData.filter((item) => item.type === 'Copy of Passport/ID').length > 0 &&
      fileData.filter((item) => item.type === 'Signed Application Form').length > 0
    );
  }, [canEditOrSubmit, fileData]);
  const canReject =
    PermissionsUtils.hasPermission(PermissionType.APPLICATIONS, PermissionLevel.EDIT) &&
    PermissionsUtils.hasPermission(PermissionType.APPLICATIONS, PermissionLevel.APPROVE) &&
    applicationData?.status === 'IppCreditAuthority';
  const canAssignPartnerAccountManager =
    PermissionsUtils.hasPermission(PermissionType.APPLICATIONS, PermissionLevel.ASSIGN) && PermissionsUtils.hasRole(RoleType.ADMIN) && !supplementary;

  const openPdf = () => {
    if (!selectedApplicationType || !selectedApplicationId) return;
    getApplicationPdf({ applicationId: selectedApplicationId, applicationType: selectedApplicationType });
  };

  useEffect(() => {
    if (loadingApproveCreditLimit || !successApprovedCreditLimit) return;
    setVersion((prev) => prev + 1);
    clearApprovedCreditLimit();
  }, [loadingApproveCreditLimit, successApprovedCreditLimit]);

  const handleRejectCreditLimit = async () => {
    setRejectType('limit');
    setRejectQuestionVisible(true);
  };

  useEffect(() => {
    if (loadingRejectCreditLimit || !successRejectedCreditLimit) return;
    setVersion((prev) => prev + 1);
    setRejectQuestionVisible(false);
    setRejectingCreditLimit(false);
    snackbar.success('Credit Limit was rejected');
    clearRejectedCreditLimit();
  }, [loadingRejectCreditLimit, successRejectedCreditLimit]);

  useEffect(() => {
    if (loadingRejectApplication || !successRejectedApplication) return;
    setVersion((prev) => prev + 1);
    setRejectQuestionVisible(false);
    setRejectingCreditLimit(false);
    snackbar.success('Application was rejected');
    clearRejectedApplication();
  }, [loadingRejectApplication, successRejectedApplication]);

  const rejectCreditLimitHandler = async () => {
    switch (rejectType) {
      case 'limit':
        rejectCreditLimit(selectedApplicationId!);
        break;
      case 'application':
        rejectApplication(selectedApplicationId!);
        break;
      default:
        break;
    }
  };

  const onSubmit = async () => {
    if (!applicationData || !selectedApplicationId) return;
    try {
      setSubmitting(true);
      setLoadingApplication(true);
      const files = await FileApi.getApplicationFiles(selectedApplicationId);
      if (files.length > 0) {
        try {
          if (supplementary) {
            const data: SupplementaryApplicationEntity = {
              ...applicationData,
            } as SupplementaryApplicationEntity;
            //if (!data.lastFourNumberOfCard) data.lastFourNumberOfCard = '4444';
            //if (!data.relationshipToMain) data.relationshipToMain = 'Child';
            ValidateForm.validateSupplementaryApplication(data);
            await ApplicationApi.saveSupplement(data);
          } else {
            ValidateForm.validateApplication(mainApplicationData!);
            if (!mainApplicationData!.acceptedTC) {
              throw new Error('You should accept Terms and Conditions');
            }
            if (mainApplicationData) {
              await ApplicationApi.saveApplication(mainApplicationData);
            }
          }
        } catch (error) {
          snackbar.error(ApiUtils.getError(error));
          setSubmitting(false);
          setLoadingApplication(false);
          return;
        }
        await ApplicationApi.submitApplication(selectedApplicationId!);
        snackbar.success('Application submitted successfully');
      } else {
        snackbar.error('Files are required for submitting');
      }
      setSubmitting(false);
      setLoadingApplication(false);
      if (files.length > 0) setVersion(version + 1);
    } catch (err) {
      setSubmitting(false);
      setLoadingApplication(false);
      snackbar.error(ApiUtils.getError(err));
    }
  };

  useEffect(() => {
    if (loadingRejectApplication2) return;
    if (errorRejectApplication2) {
      setLoadingApplication(false);
      setSubmitting(false);
    }
    if (!successRejectedApplication2) return;
    setVersion(version + 1);
    setLoadingApplication(false);
    setSubmitting(false);
    snackbar.success('Application rejected successfully');
    clearRejectedApplication2();
  }, [loadingRejectApplication2, errorRejectApplication2, successRejectedApplication2]);

  const onReject = async () => {
    if (!applicationData) return;
    setSubmitting(true);
    setLoadingApplication(true);
    rejectApplication2(selectedApplicationId!);
  };

  const managerChanged = (v: string) => {
    const manager = managers?.find((item) => item.id === v);
    setSecurityChanged(true);
    setSaveSecurityError(false);
    if (supplementary) {
      const data: SupplementaryApplicationEntity = {
        ...applicationData,
        partnerAccountManagerId: v,
        partnerAccountManagerName: manager ? manager.firstname + ' ' + manager.lastname : undefined,
        ippGroup: undefined,
      } as SupplementaryApplicationEntity;
      setApplicationData(data);
    } else {
      const data: ApplicationEntity = {
        ...applicationData,
        partnerAccountManagerId: v,
        partnerAccountManagerName: manager ? manager.firstname + ' ' + manager.lastname : undefined,
        ippGroup: undefined,
      } as ApplicationEntity;
      setApplicationData(data);
    }
  };

  const circleChanged = (v: string) => {
    setSecurityChanged(true);
    setSaveSecurityError(false);
    if (supplementary) {
      const data: SupplementaryApplicationEntity = {
        ...applicationData,
        ippGroup: v,
      } as SupplementaryApplicationEntity;
      setApplicationData(data);
    } else {
      const data: ApplicationEntity = {
        ...applicationData,
        ippGroup: v,
      } as ApplicationEntity;
      setApplicationData(data);
    }
  };

  useEffect(() => {
    if (loadingAssignApplication || !successAssignedApplication) return;
    snackbar.success('Security has been successfully updated');
    clearAssignApplication();
  }, [loadingAssignApplication, successAssignedApplication]);

  const saveSecurity = async () => {
    if (!securityChanged) return;
    if (!applicationData?.partnerAccountManagerId) return setSaveSecurityError(true);
    if (!applicationData?.ippGroup) return setSaveSecurityError(true);
    setSaveSecurityError(false);
    assignApplication({
      applicationId: selectedApplicationId!,
      data: {
        partnerAccountManager: applicationData.partnerAccountManagerId,
        ippGroup: applicationData.ippGroup,
      },
    });
  };

  return (
    <>
      <Breadcrumbs aria-label='breadcrumb' sx={{ marginBottom: '12px' }}>
        <Link sx={{ textTransform: 'uppercase', fontSize: '12px' }} underline='hover' color='inherit' href={RoutePaths.applications.onNavigate()}>
          Applications
        </Link>
        {applicationData && mainApplicationData && applicationData !== mainApplicationData && (
          <Link
            sx={{ textTransform: 'uppercase', fontSize: '12px' }}
            underline='hover'
            color='inherit'
            href={RoutePaths.applicationsDetails.onNavigate(mainApplicationData.id)}
          >
            {mainApplicationData.name}
          </Link>
        )}
        {applicationData && (
          <Link sx={{ textTransform: 'uppercase', fontSize: '12px' }} underline='none'>
            {applicationData.name}
          </Link>
        )}
      </Breadcrumbs>
      {applicationData && (
        <AppBar position='static' className={classes.appBar}>
          <Toolbar>
            <Typography component='span' className={classes.title} variant='h6' noWrap>
              {applicationData.name}
            </Typography>
            <div className={classes.grow} />
            <div>
              <Tooltip title='Print'>
                <IconButton
                  component={RouterNavLink}
                  to={getRouteToPrint(applicationData.id, applicationData.mainAppId, applicationData.applicationType)}
                  onClick={openPdf}
                  size='large'
                >
                  <PrintIcon className={classes.toolbarIcon} />
                </IconButton>
              </Tooltip>
              {canEditOrSubmit && (
                <>
                  <Tooltip title='Edit'>
                    <IconButton
                      size='large'
                      to={getRouteToEdit(applicationData.id, applicationData.mainAppId, applicationData.applicationType)}
                      component={RouterNavLink}
                    >
                      <EditIcon className={classes.toolbarIcon} />
                    </IconButton>
                  </Tooltip>
                  {canSubmit && (
                    <Tooltip title='Submit'>
                      <IconButton onClick={onSubmit} loading={submitting} size='large'>
                        <DoneIcon className={classes.toolbarIcon} />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}
              {canReject && (
                <Tooltip title='Reject'>
                  <IconButton onClick={onReject} loading={submitting} size='large'>
                    <BlockIcon className={classes.toolbarIcon} />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </Toolbar>
        </AppBar>
      )}
      <Grid container alignContent='center'>
        <Grid item xs={12} md={3}>
          <DelayedLinearProgress loading={loadingApplication} />
          <Card className={classes.card}>
            <CardHeader title='Details' />
            <CardContent>
              <Typography component='span' variant='body1'>
                <Grid container>
                  {/*<Grid item xs={12} md={12} xl={12}>
                    {applicationData ? <FormControlLabel
                        checked={applicationData?.preApproved}
                        control={<Switch color="primary" disabled={!canUpdate} />}
                        label="Pre-Approval"
                        labelPlacement="end"
                        onChange={(e: any) => {
                          handlePreApproved();
                        }}
                      />
                    : null}
                  </Grid>*/}
                  <Grid item xs={4}>
                    Full Name
                  </Grid>
                  <Grid item xs={8}>
                    {!applicationData ? LOADING_TITLE : `${applicationData.firstName} ${applicationData.lastName}`}
                  </Grid>
                  <Grid item xs={4}>
                    Contact Number
                  </Grid>
                  <Grid item xs={8}>
                    {!applicationData ? LOADING_TITLE : `${applicationData.mobileNumber ? applicationData.mobileNumber : '(none)'}`}
                  </Grid>
                  <Grid item xs={4}>
                    Date Created
                  </Grid>
                  <Grid item xs={8}>
                    {!applicationData ? LOADING_TITLE : format(parseISO(applicationData.insertedDate), 'dd MMMM yyyy')}
                  </Grid>
                  <Grid item xs={4}>
                    Owner
                  </Grid>
                  <Grid item xs={8}>
                    {!applicationData ? LOADING_TITLE : `${applicationData.userName}`}
                  </Grid>
                  <Grid item xs={4}>
                    Status
                  </Grid>
                  <Grid item xs={8}>
                    {!applicationData ? LOADING_TITLE : `${applicationData.status || '(none)'}`}
                  </Grid>
                  <Grid item xs={4}>
                    Product
                  </Grid>
                  <Grid item xs={8}>
                    {!mainApplicationData ? LOADING_TITLE : `${getApplicationProduct(applicationData, mainApplicationData)}`}
                  </Grid>
                  <Grid item xs={4}>
                    Currency
                  </Grid>
                  <Grid item xs={8}>
                    {!mainApplicationData ? LOADING_TITLE : `${getApplicationCurrency(mainApplicationData)}`}
                  </Grid>
                  <Grid item xs={4}>
                    Credit Limit
                  </Grid>
                  <Grid item xs={8}>
                    {!mainApplicationData ? LOADING_TITLE : `${getApplicationCreditLimit(mainApplicationData)}`}
                  </Grid>
                  {PermissionsUtils.hasPermission(PermissionType.APPLICATIONS, PermissionLevel.WRITE) &&
                    applicationData &&
                    applicationData.applicationType === 'Main' && (
                      <Grid item xs={8}>
                        <Button
                          variant='contained'
                          color='primary'
                          style={{ marginRight: '12px' }}
                          onClick={() =>
                            navigate(
                              mainApplicationData
                                ? RoutePaths.applicationsSupplementaryNew.onNavigate(mainApplicationData.id)
                                : RoutePaths.root.onNavigate(),
                              {
                                state: { mainApplicationId: selectedApplicationId },
                              },
                            )
                          }
                        >
                          Add Supplementary Application
                        </Button>
                      </Grid>
                    )}
                  {PermissionsUtils.hasPermission(PermissionType.CREDIT_LIMIT_REQUESTS, PermissionLevel.APPROVE) &&
                    applicationData?.status === 'IppCreditAuthority' && (
                      <>
                        <Grid item xs={8}>
                          <Button
                            variant='contained'
                            color='primary'
                            loading={loadingApproveCreditLimit}
                            style={{ marginRight: '12px' }}
                            onClick={() => approveCreditLimit(selectedApplicationId!)}
                          >
                            Approve Credit Limit
                          </Button>
                        </Grid>
                        <Grid item xs={8}>
                          <Button variant='contained' color='primary' style={{ marginRight: '12px' }} onClick={handleRejectCreditLimit}>
                            Reject Credit Limit
                          </Button>
                        </Grid>
                      </>
                    )}
                </Grid>
              </Typography>
            </CardContent>
          </Card>
          {applicationData && (
            <Card className={classes.card}>
              <CardHeader title='Security' />
              <CardContent>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    saveSecurity();
                  }}
                >
                  <Typography component='span' variant='body1'>
                    <Grid container>
                      <Grid item xs={12}>
                        <FormControl fullWidth margin='normal'>
                          <InputLabel>Owner</InputLabel>
                          <Select
                            variant='standard'
                            name='partnerAccountManagerId'
                            label='Partner Account Manager'
                            required
                            fullWidth
                            readOnly={!canAssignPartnerAccountManager}
                            value={applicationData?.partnerAccountManagerId ?? ''}
                            onChange={(e) => managerChanged(e.target.value)}
                          >
                            {managers!.map((item, idx) => (
                              <MenuItem key={idx} value={item.id}>
                                {item.firstname + ' ' + item.lastname}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl fullWidth margin='normal' required>
                          <InputLabel>Circle</InputLabel>
                          <Select
                            variant='standard'
                            name='ippGroup'
                            label='Circle'
                            fullWidth
                            required
                            value={applicationData?.ippGroup ?? ''}
                            readOnly={!canAssignPartnerAccountManager}
                            onChange={(e) => circleChanged(e.target.value)}
                          >
                            {currentManager &&
                              currentManager.circles.map((item, idx) => (
                                <MenuItem key={idx} value={item}>
                                  {item}
                                </MenuItem>
                              ))}
                          </Select>
                          {saveSecurityError && <FormHelperText error>This is required!</FormHelperText>}
                        </FormControl>
                      </Grid>
                    </Grid>
                    {!supplementary && securityChanged && (
                      <Grid container style={{ justifyContent: 'flex-end' }}>
                        <Grid item xs={12} style={{ justifyContent: 'flex-end', display: 'flex' }}>
                          <Button type='submit' variant='contained' color='primary' loading={loadingAssignApplication}>
                            Save
                          </Button>
                        </Grid>
                      </Grid>
                    )}
                  </Typography>
                </form>
              </CardContent>
            </Card>
          )}
        </Grid>
        <Grid item xs={12} md={9}>
          <DelayedLinearProgress
            loading={loadingApplicationFiles || loadingDownloadApplicationFile || loadingDeleteApplicationFile || loadingApplication}
          />
          <Card>
            <CardHeader title='Files' />
            {!fileData.length &&
              !loadingApplication &&
              !loadingApplicationFiles &&
              !loadingDownloadApplicationFile &&
              !loadingDeleteApplicationFile && (
                <Typography component='div' align='center' sx={{ padding: '16px 16px 24px 16px' }}>
                  No data to display.
                </Typography>
              )}
            {!!fileData.length && <FileList data={fileData} deleteFile={canUpdate ? deleteApplicationFiles : void 0} />}
          </Card>
          <br />
          <Grid item xs={12} md={12}>
            {PermissionsUtils.hasPermission(PermissionType.APPLICATIONS, PermissionLevel.WRITE) &&
              PermissionsUtils.hasPermission(PermissionType.IPPFILES, PermissionLevel.WRITE) &&
              applicationData &&
              applicationData.status === 'Draft' && (
                <FileUpload
                  countries={countries}
                  uploadedFiles={fileData}
                  nationalitiesCount={applicationData?.nationalities?.length}
                  onUpload={() => setFileReload((curFileReload) => !curFileReload)}
                />
              )}
          </Grid>
        </Grid>
      </Grid>
      {file?.data && <PDFView objectUrl={URL.createObjectURL(file.data)} onClose={clearApplicationPdf} />}
      <Dialog open={rejectQuestionVisible}>
        <DialogTitle>What should be rejected?</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin='normal'>
            <RadioGroup row>
              <FormControlLabel
                checked={rejectType === 'limit'}
                value='limit'
                control={<Radio color='primary' />}
                label='Credit Limit'
                labelPlacement='end'
                onChange={() => setRejectType('limit')}
              />
              <FormControlLabel
                checked={rejectType === 'application'}
                value='application'
                control={<Radio color='primary' />}
                label='Whole Application'
                labelPlacement='end'
                onChange={() => setRejectType('application')}
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button loading={rejectingCreditLimit} onClick={rejectCreditLimitHandler}>
            Reject
          </Button>
          <Button autoFocus onClick={() => setRejectQuestionVisible(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
